import React, { useEffect } from 'react';
import { Form, Modal, Select, Switch, Tag } from 'antd';
import { useForm } from 'antd/es/form/Form';
import { AdminWebinarEvent, CreateWebinarOverlayPayload, UpdateWebinarOverlayPayload } from 'api/webinarAdmin';

export interface RoleOption {
  value: number;
  label: string;
}

interface WebinarEventFormValues {
  isExclusive: boolean;
  roleIds: number[];
  isActive: boolean;
}

interface Props {
  open: boolean;
  saving: boolean;
  /** 'create' adds the event as a Vantage overlay; 'edit' updates an existing overlay. */
  mode: 'create' | 'edit';
  /** The target event (an unlinked Sequel event when creating, an overlay row when editing). */
  event?: AdminWebinarEvent;
  roleOptions: RoleOption[];
  onCancel: () => void;
  onCreate: (payload: CreateWebinarOverlayPayload) => void;
  onUpdate: (id: number, payload: UpdateWebinarOverlayPayload) => void;
}

/**
 * Create/edit modal for webinar overlay rows. The target Sequel event is always
 * known (selected from a table row), so only overlay attributes are editable.
 */
const WebinarEventModal: React.FC<Props> = ({
  open,
  saving,
  mode,
  event,
  roleOptions,
  onCancel,
  onCreate,
  onUpdate
}) => {
  const [form] = useForm<WebinarEventFormValues>();
  const isEditing = mode === 'edit';
  const isExclusive = Form.useWatch('isExclusive', form);

  useEffect(() => {
    if (!open || !event) {
      return;
    }

    if (isEditing) {
      form.setFieldsValue({
        isExclusive: event.isExclusive,
        roleIds: event.roleIds ?? [],
        isActive: event.isActive
      });
    } else {
      form.setFieldsValue({
        isExclusive: false,
        roleIds: [],
        isActive: true
      });
    }
  }, [open, event, isEditing, form]);

  const handleOk = async (): Promise<void> => {
    if (!event) {
      return;
    }

    const values = await form.validateFields();
    const roleIds = values.isExclusive ? (values.roleIds ?? []) : [];

    if (isEditing && event.dbId !== undefined) {
      onUpdate(event.dbId, {
        isExclusive: values.isExclusive,
        roleIds,
        isActive: values.isActive
      });
      return;
    }

    onCreate({
      sequelEventId: event.sequelEventId,
      isExclusive: values.isExclusive,
      roleIds,
      isActive: values.isActive
    });
  };

  return (
    <Modal
      open={open}
      title={isEditing ? 'Edit Webinar' : 'Add Webinar to Vantage'}
      okText={isEditing ? 'Save' : 'Add Webinar'}
      onCancel={onCancel}
      onOk={handleOk}
      confirmLoading={saving}
      destroyOnClose
      width={560}
    >
      <Form layout="vertical" form={form}>
        <Form.Item label="Webinar">
          <div className="font-medium">{event?.name}</div>
          <Tag className="mt-1">{event?.sequelEventId}</Tag>
        </Form.Item>

        <Form.Item
          label="Vantage Exclusive"
          name="isExclusive"
          valuePropName="checked"
          tooltip="Exclusive webinars are only visible inside Vantage and can be restricted by role."
        >
          <Switch checkedChildren="Exclusive" unCheckedChildren="Public" />
        </Form.Item>

        <Form.Item
          label="Visible to Roles"
          name="roleIds"
          tooltip="Leave empty to make the exclusive webinar visible to all Vantage users."
        >
          <Select
            mode="multiple"
            allowClear
            disabled={!isExclusive}
            placeholder={isExclusive ? 'All Vantage users (no roles selected)' : 'Enable exclusive to select roles'}
            options={roleOptions}
            optionFilterProp="label"
          />
        </Form.Item>

        <Form.Item
          label="Active"
          name="isActive"
          valuePropName="checked"
          tooltip="Inactive webinars stay in the overlay but are hidden from users."
        >
          <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default WebinarEventModal;
